import { motion } from 'motion/react';
import { Star, ArrowRight, Headphones, TrendingUp } from 'lucide-react';

interface CallToActionProps {
  onContact?: () => void;
}

export const CallToAction = ({ onContact }: CallToActionProps) => {
  return (
    <section className="relative bg-gradient-to-r from-[#9333ea] via-[#db2777] to-[#f59e0b] overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              radial-gradient(circle at 25% 25%, #ffffff40 2px, transparent 2px),
              radial-gradient(circle at 75% 75%, #ffffff60 1px, transparent 1px),
              linear-gradient(45deg, transparent 40%, #ffffff20 50%, transparent 60%)
            `,
            backgroundSize: '60px 60px, 40px 40px, 80px 80px'
          }}
        />
      </div>

      {/* Floating Elements */}
      <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl animate-pulse" />
      <div className="absolute top-20 right-20 w-32 h-32 bg-white/5 rounded-full blur-2xl animate-pulse delay-1000" />
      <div className="absolute bottom-10 left-1/4 w-24 h-24 bg-white/10 rounded-full blur-xl animate-pulse delay-500" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 py-8 md:py-14">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-3"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 border border-white/30 backdrop-blur-md shadow-xl rounded-full mb-2">
              <Star className="w-3.5 h-3.5 fill-current text-white animate-pulse" />
              <span className="text-xs font-bold uppercase tracking-wider text-white">Start Your Journey</span>
            </div>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-2 leading-[1.1] drop-shadow-lg"
          >
            Ready to Start Your Collaboration
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-base md:text-lg text-white/90 font-medium mb-6 leading-relaxed max-w-2xl mx-auto"
          >
            Join 50+ institutions that trust Anuvadini to bridge language barriers and drive engagement.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row gap-3 justify-center items-center relative z-20"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onContact || (() => console.log('Contact clicked'))}
              className="group px-6 py-2.5 bg-white text-[var(--gradient-color-1)] font-bold text-sm uppercase tracking-wider rounded-full shadow-2xl hover:shadow-white/20 transition-all duration-300 flex items-center gap-2"
            >
              Start Your Journey
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.location.href = '/#services'}
              className="px-6 py-2.5 bg-transparent border-2 border-white text-white font-bold text-sm uppercase tracking-wider rounded-full hover:bg-white/10 transition-all duration-300"
            >
              Explore Services
            </motion.button>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true }}
            className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4"
          >
            <div className="text-center group">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center mx-auto mb-2 group-hover:bg-white/30 transition-colors duration-300">
                <Star className="w-5 h-5 text-yellow-300" />
              </div>
              <div className="text-xl font-black text-white mb-0.5">4.9/5</div>
              <div className="text-[10px] text-white/80 font-medium uppercase tracking-wider">Partner Rating</div>
            </div>

            <div className="text-center group">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center mx-auto mb-2 group-hover:bg-white/30 transition-colors duration-300">
                <TrendingUp className="w-5 h-5 text-green-300" />
              </div>
              <div className="text-xl font-black text-white mb-0.5">95%</div>
              <div className="text-[10px] text-white/80 font-medium uppercase tracking-wider">Success Rate</div>
            </div>

            <div className="text-center group">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center mx-auto mb-2 group-hover:bg-white/30 transition-colors duration-300">
                <Headphones className="w-5 h-5 text-blue-300" />
              </div>
              <div className="text-xl font-black text-white mb-0.5">24/7</div>
              <div className="text-[10px] text-white/80 font-medium uppercase tracking-wider">Support</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
